/**
 * Circuits described in words ("cuisine : 6 prises 20 A 2,5 mm²") → circuit specs.
 * The schema is what the model returns server-side; `parseCircuitText` is a local, rule-based
 * parser used when no AI service is configured (and as a sanity baseline for the model).
 */
import { z } from 'zod';
import type { CircuitSpec, PointSpec } from '@elec/editor';
import { searchSymbols, SYMBOLS } from '@elec/symbols';

export const describedCircuitSchema = z.object({
  label: z.string().describe('Short circuit description, e.g. "Prises cuisine"'),
  device: z.enum(['MCB', 'RCBO', 'fuse']).default('MCB'),
  ratingA: z.number().positive().nullable(),
  curve: z.enum(['B', 'C', 'D']).nullable(),
  points: z.array(
    z.object({
      kind: z.enum(['socket', 'light', 'appliance', 'switch']),
      /** Appliance symbol id from the library when kind = appliance, e.g. "iec.dishwasher";
       * "iec.socket-3p" for a three-phase socket (kind = socket). */
      symbolId: z.string().nullable(),
      count: z.number().int().min(1).max(20),
      /** Power of each point in kW, when the user gave it (an oven of 3.5 kW): feeds Ib and ΔU. */
      powerKw: z.number().positive().max(100).nullable().optional(),
    }),
  ),
  cable: z
    .object({
      type: z.string(),
      cores: z.number().int(),
      sectionMm2: z.number().positive(),
      /** Cable length in metres, when the user gave it: feeds the voltage drop. */
      lengthM: z.number().positive().max(1000).nullable().optional(),
    })
    .nullable(),
  /** Sensitivity of the RCD the user asked for (30, 300…), if stated. */
  rcdMa: z.number().positive().nullable(),
});

export const describedCircuitsSchema = z.object({ circuits: z.array(describedCircuitSchema) });
export type DescribedCircuit = z.infer<typeof describedCircuitSchema>;

export const APPLIANCE_IDS = SYMBOLS.filter((s) => s.category === 'appliance' || s.category === 'motor').map(
  (s) => s.id,
);

/**
 * A cable designation (XVB, XGB, EXVB, VOB, H07V-U…), or '' when the model put something else in
 * the field: the conductor material ("cuivre", "Cu") or the section again ("3G2,5").
 */
export function cableType(t: string): string {
  const v = t.trim().toUpperCase();
  return /^[A-Z][A-Z0-9-]{1,9}$/.test(v) && !/^(CU|CUIVRE|COPPER|KOPER|AL|ALU|ALUMINIUM)$/.test(v) ? v : '';
}

export function describedToSpec(d: DescribedCircuit): CircuitSpec & { rcdMa: number | null } {
  const points: PointSpec[] = [];
  for (const p of d.points) {
    const symbolId =
      p.kind === 'socket'
        ? // A three-phase socket keeps its symbol; any other socket is a single-phase earthed one.
          p.symbolId === 'iec.socket-3p'
          ? 'iec.socket-3p'
          : 'iec.socket'
        : p.kind === 'light'
          ? 'iec.light'
          : p.kind === 'switch'
            ? 'iec.switch'
            : p.symbolId && APPLIANCE_IDS.includes(p.symbolId)
              ? p.symbolId
              : 'iec.appliance';
    const props = p.powerKw ? { powerKw: p.powerKw } : undefined;
    for (let i = 0; i < p.count; i++) points.push(props ? { symbolId, props: { ...props } } : { symbolId });
  }
  // Not given = unknown (''), not the symbol's default 16 A curve C: R-DEV-01 asks for it.
  const props: Record<string, string | number> = { ratingA: d.ratingA ?? '' };
  if (d.device !== 'fuse') props.curve = d.curve ?? '';
  // An RCBO is its own RCD: it takes the asked sensitivity and is not placed under another RCD.
  const rcbo = d.device === 'RCBO';
  if (rcbo && d.rcdMa) props.sensitivityMa = d.rcdMa;
  return {
    label: d.label,
    device: {
      symbolId: d.device === 'RCBO' ? 'iec.rcbo' : d.device === 'fuse' ? 'iec.fuse' : 'iec.breaker',
      props,
    },
    points,
    ...(d.cable
      ? {
          cable: {
            type: cableType(d.cable.type),
            cores: d.cable.cores,
            sectionMm2: d.cable.sectionMm2,
            ...(d.cable.lengthM ? { lengthM: d.cable.lengthM } : {}),
          },
        }
      : {}),
    rcdMa: rcbo ? null : d.rcdMa,
  };
}

const WORD_NUM: Record<string, number> = {
  un: 1,
  une: 1,
  one: 1,
  deux: 2,
  two: 2,
  trois: 3,
  three: 3,
  quatre: 4,
  four: 4,
  cinq: 5,
  five: 5,
  six: 6,
  sept: 7,
  seven: 7,
  huit: 8,
  eight: 8,
  een: 1,
  één: 1,
  twee: 2,
  drie: 3,
  vier: 4,
  vijf: 5,
  zes: 6,
  zeven: 7,
  acht: 8,
};
const num = (s: string | undefined) =>
  s === undefined ? 1 : /^\d+$/.test(s) ? Number(s) : (WORD_NUM[s.toLowerCase()] ?? 1);
const N =
  '(\\d+|un|une|one|deux|two|trois|three|quatre|four|cinq|five|six|sept|seven|huit|eight|een|één|twee|drie|vier|vijf|zes|zeven|acht)';

/**
 * Local parser: one circuit per line (or per ";"). Understands counts of sockets, lighting
 * points and switches, appliance names from the symbol library, "20 A" / "C20", "2,5 mm²"
 * or "3G2,5", and "30 mA".
 */
export function parseCircuitText(text: string): DescribedCircuit[] {
  const out: DescribedCircuit[] = [];
  for (const raw of text.split(/[\n;]+/)) {
    const line = raw.trim();
    if (!line) continue;
    const lower = line.toLowerCase();
    const label = line
      .split(/\s*[:,]\s*|\s[–—-]\s/)[0]!
      .replace(
        /\b[BCD]\d{1,2}\b|\b[BCD]?\d{1,2}\s?A\b|\d{2,3}\s?mA|\d+(?:[.,]\d+)?\s?mm²?|\d\s?G\s?\d+(?:[.,]\d+)?|\b(?:XVB|VOB|EXVB|XGB|VVB)\b/gi,
        '',
      )
      .replace(/\s+/g, ' ')
      .trim();
    const points: DescribedCircuit['points'] = [];
    const sock = new RegExp(`${N}?\\s*(prises?|sockets?|stopcontact(?:en)?)`, 'i').exec(lower);
    if (sock) points.push({ kind: 'socket', symbolId: null, count: num(sock[1]) });
    const light = new RegExp(
      `${N}?\\s*(points? lumineux|lampes?|lights?|lighting points?|lichtpunt(?:en)?|lampen|luminaires?)`,
      'i',
    ).exec(lower);
    if (light) points.push({ kind: 'light', symbolId: null, count: num(light[1]) });
    const sw = new RegExp(`${N}?\\s*(interrupteurs?|switch(?:es)?|schakelaars?)`, 'i').exec(lower);
    if (sw) points.push({ kind: 'switch', symbolId: null, count: num(sw[1]) });
    if (!points.length) {
      // Try appliance names: "lave-vaisselle", "borne de recharge", "dishwasher"…
      const words = lower.replace(/[^\p{L}\s-]/gu, ' ');
      const pool = SYMBOLS.filter((s) => APPLIANCE_IDS.includes(s.id) && s.id !== 'iec.appliance');
      const hit = pool.find((s) =>
        [s.name.fr, s.name.en, s.name.nl, ...(s.keywords ?? [])].some((k) =>
          words.includes(k.toLowerCase().split(' / ')[0]!),
        ),
      );
      const found = hit ?? searchSymbols(label, pool)[0];
      if (found) points.push({ kind: 'appliance', symbolId: found.id, count: 1 });
    }
    const curveRating = /\b([BCD])\s?(\d{1,2})\b/.exec(line);
    const rating = /(\d{1,2})\s?A\b/.exec(line);
    const section = /(\d+(?:[.,]\d+)?)\s?mm/i.exec(line) ?? /\d\s?G\s?(\d+(?:[.,]\d+)?)/i.exec(line);
    const cores = /(\d)\s?G\s?\d/i.exec(line);
    const rcd = /(\d{2,3})\s?mA/i.exec(line);
    out.push({
      label,
      device: 'MCB',
      ratingA: curveRating ? Number(curveRating[2]) : rating ? Number(rating[1]) : null,
      // A rating without a curve: the usual one (B for lighting, C otherwise), as the assistant does.
      curve: curveRating
        ? (curveRating[1] as 'B' | 'C' | 'D')
        : rating
          ? points.length && points.every((p) => p.kind === 'light')
            ? 'B'
            : 'C'
          : null,
      points,
      cable: section
        ? {
            type: /\b(xvb|vob|exvb|xgb|vvb)\b/i.exec(line)?.[1]?.toUpperCase() ?? 'XVB',
            cores: cores ? Number(cores[1]) : 3,
            sectionMm2: Number(section[1]!.replace(',', '.')),
          }
        : null,
      rcdMa: rcd ? Number(rcd[1]) : null,
    });
  }
  return out;
}
